import { useState, useCallback } from "react";
import type { DocumentEntity } from "../types/strapi";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import esLocale from "i18n-iso-countries/langs/es.json";
import i18n from "../i18n";

countries.registerLocale(enLocale);
countries.registerLocale(esLocale);

interface FilterState {
  selectedAuthors: string[];
  selectedDocTypes: string[];
  selectedCategories: string[];
  selectedCountries: string[];
  selectedShowcases: string[];
  selectedDublinCoreTypes: string[];
  selectedKeywords: string[];
}

interface FilteredStates {
  authors: string[];
  docTypes: string[];
  categories: string[];
  countries: string[];
  showcases: string[];
  dublinCoreTypes: string[];
  keywords: string[];
}

interface UseFiltersReturn {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  filteredStates: FilteredStates;
  filterItems: (items: DocumentEntity[]) => DocumentEntity[];
  updateFilteredStates: (items: DocumentEntity[]) => void;
  loadSessionData: () => void;
  saveSessionData: () => void;
}

export function useFilters(): UseFiltersReturn {
  const [filters, setFilters] = useState<FilterState>({
    selectedAuthors: [],
    selectedDocTypes: [],
    selectedCategories: [],
    selectedCountries: [],
    selectedShowcases: [],
    selectedDublinCoreTypes: [],
    selectedKeywords: [],
  });

  const [filteredStates, setFilteredStates] = useState<FilteredStates>({
    authors: [],
    docTypes: [],
    categories: [],
    countries: [],
    showcases: [],
    dublinCoreTypes: [],
    keywords: [],
  });

  const extractUniqueValues = useCallback(
    (items: DocumentEntity[], keyPath: string[], isArray = false): string[] => {
      const values = items.flatMap((item) => {
        const value = keyPath.reduce(
          (acc: unknown, key: string) =>
            (acc as Record<string, unknown>)?.[key] ?? null,
          item?.contentType
        );
        if (isArray && Array.isArray(value)) {
          return value.map((v: Record<string, unknown>) =>
            (v as Record<string, unknown>)?.name?.toString()?.trim()
          );
        }
        return value ? [(value as string).trim()] : [];
      });

      return [...new Set(values.filter(Boolean))].sort((a, b) =>
        a
          .replace(/[.,]/g, "")
          .toLowerCase()
          .localeCompare(b.replace(/[.,]/g, "").toLowerCase(), undefined, {
            sensitivity: "base",
          })
      );
    },
    []
  );

  const extractCountryNames = useCallback(
    (items: DocumentEntity[]): string[] => {
      const names = items
        .map((item) => {
          const code = item?.contentType?.country;
          return code ? countries.getName(code, i18n.language) : null;
        })
        .filter(Boolean) as string[];
      return [...new Set(names)].sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase(), undefined, {
          sensitivity: "base",
        })
      );
    },
    []
  );

  const loadSessionData = useCallback((): void => {
    const storedFilters = [
      "Authors",
      "DocTypes",
      "Categories",
      "Countries",
      "Showcases",
      "DublinCoreTypes",
      "Keywords",
    ].reduce(
      (acc, key) => ({
        ...acc,
        [`selected${key}`]: JSON.parse(
          sessionStorage.getItem(`selected${key}`) || "[]"
        ),
      }),
      {}
    );
    setFilters((prev) => ({ ...prev, ...storedFilters }));
  }, []);

  const saveSessionData = useCallback((): void => {
    Object.entries(filters).forEach(([key, value]) => {
      sessionStorage.setItem(key, JSON.stringify(value));
    });
  }, [filters]);

  const filterItems = useCallback(
    (items: DocumentEntity[]): DocumentEntity[] =>
      items.filter((item) => {
        const content = item?.contentType;
        const keywords =
          content?.keywords?.map((kw) =>
            kw?.name?.toString()?.trim()
          ) || [];

        return (
          (!filters.selectedAuthors.length ||
            filters.selectedAuthors.includes(content?.author?.trim() || "")) &&
          (!filters.selectedDocTypes.length ||
            filters.selectedDocTypes.includes(
              content?.doc_type?.name?.trim() || ""
            )) &&
          (!filters.selectedCategories.length ||
            filters.selectedCategories.includes(
              content?.category?.name?.trim() || ""
            )) &&
          (!filters.selectedCountries.length ||
            filters.selectedCountries.includes(
              countries.getName(content?.country || "", i18n.language) || ""
            )) &&
          (!filters.selectedShowcases.length ||
            filters.selectedShowcases.includes(
              content?.showcase?.name?.trim() || ""
            )) &&
          (!filters.selectedDublinCoreTypes.length ||
            filters.selectedDublinCoreTypes.includes(
              content?.dublin_core_type?.name?.toString()?.trim() || ""
            )) &&
          (!filters.selectedKeywords.length ||
            filters.selectedKeywords.every((kw) =>
              keywords.includes(kw.trim())
            ))
        );
      }),
    [filters]
  );

  const updateFilteredStates = useCallback(
    (items: DocumentEntity[]): void => {
      setFilteredStates({
        authors: extractUniqueValues(items, ["author"]),
        docTypes: extractUniqueValues(items, [
          "doc_type",
          "name",
        ]),
        categories: extractUniqueValues(items, [
          "category",
          "name",
        ]),
        countries: extractCountryNames(items),
        showcases: extractUniqueValues(items, [
          "showcase",
          "name",
        ]),
        dublinCoreTypes: extractUniqueValues(items, [
          "dublin_core_type",
          "name",
        ]),
        keywords: extractUniqueValues(items, ["keywords"], true),
      });
    },
    [extractUniqueValues, extractCountryNames]
  );

  return {
    filters,
    setFilters,
    filteredStates,
    filterItems,
    updateFilteredStates,
    loadSessionData,
    saveSessionData,
  };
}

export default useFilters;
